const RECHARGE_ONE_TIME_SELECTORS = [
  '.rc-selection__onetime .rc-price',
  '.rc-purchase-option__onetime .rc-purchase-option__price',
  '.rc-purchase-option__price',
  '[part~="rc-purchase-option__price"]',
];

const RECHARGE_SUBSCRIPTION_SELECTORS = [
  '.rc-purchase-option__subscription',
  '[part~="rc-purchase-option__subscription"]',
  '.rc-selection__subscription',
];

const RECHARGE_ORIGINAL_PRICE_SELECTOR =
  '[part~="rc-purchase-option__original-price"], .rc-price.strike-through';

const RECHARGE_DISCOUNTED_PRICE_SELECTOR =
  '[part~="rc-purchase-option__discounted-price"]';

const RECHARGE_PLAIN_PRICE_SELECTOR =
  '[part~="rc-purchase-option__price"], .rc-purchase-option__prices .rc-price';

class ProductQuantityPrice {
  constructor(priceContainer) {
    this.priceContainer = priceContainer;
    this.sectionId = priceContainer.dataset.section;
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    this.productSection = priceContainer.closest('section') || priceContainer;
    this.observedRechargeRoots = new WeakSet();
    this.rechargeObservers = [];
    this.rechargePriceStates = new WeakMap();
    this.rechargeRetryTimer = null;
    this.rechargeRetryAttempts = 0;

    if (!this.quantityInput) return;

    this.updatePrice = this.updatePrice.bind(this);
    this.quantityInput.addEventListener('input', this.updatePrice);
    this.quantityInput.addEventListener('change', this.updatePrice);

    this.productObserver = new MutationObserver(this.updatePrice);
    this.productObserver.observe(this.productSection, {
      childList: true,
      subtree: true,
    });

    this.updatePrice();
  }

  formatMoney(cents) {
    const format = this.priceContainer.dataset.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) return (cents / 100).toFixed(2);

    const formatWithDelimiters = (
      value,
      precision = 2,
      thousands = ',',
      decimal = '.'
    ) => {
      const number = Number(value);

      if (!Number.isFinite(number)) return '0';

      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };

    let value;

    switch (placeholder[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents);
        break;
    }

    return format.replace(placeholder[0], value);
  }

  setPrice(selector, cents) {
    const priceElement = this.priceContainer.querySelector(selector);
    if (!priceElement) return;

    const formattedPrice = this.formatMoney(cents).trim();
    if (priceElement.textContent.trim() !== formattedPrice) {
      priceElement.textContent = formattedPrice;
    }
  }

  parseMoney(text) {
    if (typeof text !== 'string') return NaN;

    const numeric = text.replace(/[^\d.,]/g, '');
    if (!numeric) return NaN;

    const decimals = numeric.match(/[.,](\d{1,2})$/);
    let normalized;

    if (decimals) {
      const separatorIndex = numeric.lastIndexOf(decimals[0].charAt(0));
      normalized = `${numeric
        .slice(0, separatorIndex)
        .replace(/[.,]/g, '')}.${numeric.slice(separatorIndex + 1)}`;
    } else {
      normalized = numeric.replace(/[.,]/g, '');
    }

    const amount = Number.parseFloat(normalized);

    return Number.isFinite(amount) ? Math.round(amount * 100) : NaN;
  }

  getAmountTextNode(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let amountNode = null;
    let node;

    while ((node = walker.nextNode())) {
      if (/\d/.test(node.nodeValue)) amountNode = node;
    }

    return amountNode;
  }

  getAmountText(element) {
    const amountNode = this.getAmountTextNode(element);

    return (amountNode ? amountNode.nodeValue : element.textContent).trim();
  }

  getRechargePriceState(element) {
    if (!this.rechargePriceStates.has(element)) {
      this.rechargePriceStates.set(element, { writtenText: null, ratio: null });
    }

    return this.rechargePriceStates.get(element);
  }

  writeAmount(element, cents) {
    const formattedPrice = this.formatMoney(cents).trim();
    const amountNode = this.getAmountTextNode(element);

    // Remember what was written so a later render by the widget itself can be
    // told apart from this script's own output.
    this.getRechargePriceState(element).writtenText = formattedPrice;

    // Replace only the text node holding the amount so the visually hidden
    // labels the Recharge widget renders alongside it are preserved.
    if (amountNode) {
      if (amountNode.nodeValue.trim() !== formattedPrice) {
        amountNode.nodeValue = formattedPrice;
      }
      return;
    }

    if (element.textContent.trim() !== formattedPrice) {
      element.textContent = formattedPrice;
    }
  }

  setRechargePrices(unitPrice, quantity) {
    const rechargeRoots = this.getRechargeRoots();
    const updatedPrices =
      this.setRechargeOneTimePrices(rechargeRoots, unitPrice * quantity) +
      this.setRechargeSubscriptionPrices(rechargeRoots, unitPrice, quantity);

    const rechargeWidget = this.productSection.querySelector(
      'recharge-subscription-widget'
    );

    if (rechargeWidget && updatedPrices === 0) {
      this.scheduleRechargeRetry();
    } else if (updatedPrices > 0) {
      this.rechargeRetryAttempts = 0;
    }
  }

  setRechargeOneTimePrices(rechargeRoots, cents) {
    const rechargePrices = new Set();

    rechargeRoots.forEach((root) => {
      RECHARGE_ONE_TIME_SELECTORS.forEach((selector) => {
        root.querySelectorAll(selector).forEach((price) => {
          // Subscription prices carry a discount, so they are handled apart.
          if (price.closest(RECHARGE_SUBSCRIPTION_SELECTORS.join(', '))) return;

          rechargePrices.add(price);
        });
      });
    });

    rechargePrices.forEach((price) => this.writeAmount(price, cents));

    return rechargePrices.size;
  }

  setRechargeSubscriptionPrices(rechargeRoots, unitPrice, quantity) {
    const purchaseOptions = new Set();

    rechargeRoots.forEach((root) => {
      RECHARGE_SUBSCRIPTION_SELECTORS.forEach((selector) => {
        root.querySelectorAll(selector).forEach((option) => purchaseOptions.add(option));
      });
    });

    let updatedPrices = 0;

    purchaseOptions.forEach((option) => {
      const originalPrice = option.querySelector(RECHARGE_ORIGINAL_PRICE_SELECTOR);
      const discountedPrice = option.querySelector(RECHARGE_DISCOUNTED_PRICE_SELECTOR);

      if (!originalPrice && !discountedPrice) {
        // No subscription discount, so the price matches the one-time total.
        option.querySelectorAll(RECHARGE_PLAIN_PRICE_SELECTOR).forEach((price) => {
          this.writeAmount(price, unitPrice * quantity);
          updatedPrices += 1;
        });

        return;
      }

      if (discountedPrice) {
        const discountRatio = this.getRechargeDiscountRatio(discountedPrice, unitPrice);

        if (discountRatio) {
          this.writeAmount(
            discountedPrice,
            Math.round(unitPrice * discountRatio) * quantity
          );
          updatedPrices += 1;
        }
      }

      if (originalPrice) {
        this.writeAmount(originalPrice, unitPrice * quantity);
        updatedPrices += 1;
      }
    });

    return updatedPrices;
  }

  getRechargeDiscountRatio(discountedPrice, unitPrice) {
    const state = this.getRechargePriceState(discountedPrice);
    const currentText = this.getAmountText(discountedPrice);

    // Whatever the widget renders itself is a single unit price, which gives
    // the subscription discount. A value written by this script is already a
    // quantity total, so the discount is taken from the last known ratio.
    if (state.writtenText !== currentText) {
      const ratio = this.parseMoney(currentText) / unitPrice;

      if (Number.isFinite(ratio) && ratio > 0 && ratio <= 1) state.ratio = ratio;
    }

    return state.ratio;
  }

  getRechargeRoots() {
    const rechargeRoots = [this.productSection];

    const collectShadowRoots = (root) => {
      root.querySelectorAll('*').forEach((element) => {
        if (!element.shadowRoot || rechargeRoots.includes(element.shadowRoot)) return;

        rechargeRoots.push(element.shadowRoot);
        this.observeRechargeRoot(element.shadowRoot);
        collectShadowRoots(element.shadowRoot);
      });
    };

    this.productSection
      .querySelectorAll('recharge-subscription-widget')
      .forEach((widget) => {
        if (!widget.shadowRoot) return;

        rechargeRoots.push(widget.shadowRoot);
        this.observeRechargeRoot(widget.shadowRoot);
        collectShadowRoots(widget.shadowRoot);
      });

    return rechargeRoots;
  }

  observeRechargeRoot(root) {
    if (this.observedRechargeRoots.has(root)) return;

    const observer = new MutationObserver(this.updatePrice);
    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    this.observedRechargeRoots.add(root);
    this.rechargeObservers.push(observer);
  }

  scheduleRechargeRetry() {
    if (this.rechargeRetryTimer || this.rechargeRetryAttempts >= 80) return;

    this.rechargeRetryTimer = window.setTimeout(() => {
      this.rechargeRetryTimer = null;
      this.rechargeRetryAttempts += 1;
      this.updatePrice();
    }, 250);
  }

  updatePrice() {
    const price = this.priceContainer.querySelector('.price[data-unit-price]');
    if (!price || !this.quantityInput) return;

    const quantity = Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
    const unitPrice = Number(price.dataset.unitPrice);
    const compareAtPrice = Number(price.dataset.compareAtPrice);

    if (!Number.isFinite(unitPrice)) return;

    const totalPrice = unitPrice * quantity;
    this.setPrice('.price__regular .price-item--regular', totalPrice);
    this.setPrice('.price__sale .price-item--sale', totalPrice);
    this.setRechargePrices(unitPrice, quantity);

    if (Number.isFinite(compareAtPrice) && compareAtPrice > unitPrice) {
      this.setPrice(
        '.price__sale s.price-item--regular',
        compareAtPrice * quantity
      );
    }
  }
}

function initializeProductQuantityPrices(root = document) {
  root.querySelectorAll('[data-product-quantity-price]').forEach((priceContainer) => {
    if (priceContainer.productQuantityPrice) return;

    priceContainer.productQuantityPrice = new ProductQuantityPrice(priceContainer);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeProductQuantityPrices());
} else {
  initializeProductQuantityPrices();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeProductQuantityPrices(event.target);
});
